import { SessionType } from 'apollo/generated/client-operations';
import { logout, refreshJwt, trackUserSession } from 'auth/fusionauth';
import { AuthState, SessionFetcher } from 'auth/types';
import { appConfig } from 'config/app';
import React, { MutableRefObject, useCallback, useMemo, useRef } from 'react';
import { clearSessionCookie, setJwtCookie } from 'utils/cookies';
import { LocalStorageHookTuple, useLocalStorage } from 'utils/useLocalStorage';

const AUTH_REDIRECT_KEY = 'auth_redirected_from';

export const useAuthRedirectStorage = (
  defaultValue?: string,
): LocalStorageHookTuple => {
  const [value, setValue, clearValue] = useLocalStorage(
    AUTH_REDIRECT_KEY,
    defaultValue,
  );
  return [value, setValue, clearValue];
};

/**
 * Read and erase the stored pre-login path in one step. The path is single
 * use: consuming it on the post-login redirect keeps a path that no longer
 * resolves from replaying as a 404 on every later login.
 */
export const consumeAuthRedirect = (): string | null => {
  const value = window.localStorage.getItem(AUTH_REDIRECT_KEY);
  if (value != null) {
    window.localStorage.removeItem(AUTH_REDIRECT_KEY);
  }
  return value;
};

export const useReportingRedirectStorage = (
  defaulValue?: string,
): LocalStorageHookTuple => {
  const [value, setValue, clearValue] = useLocalStorage(
    'reporting_redirected_from',
    defaulValue,
  );

  return [value, setValue, clearValue];
};

/**
 * Wrapper around the useLocalStorage hook that checks for nullish string values
 * and clears the storage if one happens to be passed
 *
 * @param storageKey - LocalStorage Key to use
 * @param defaultValue - optional default value to set
 * @returns
 */
const useTokenStorage = (
  storageKey: string,
  defaultValue?: string,
): LocalStorageHookTuple => {
  const isValid = (checkedValue?: string): boolean => {
    return (
      checkedValue != null &&
      checkedValue !== 'null' &&
      checkedValue !== 'undefined'
    );
  };
  const checkedDefaultValue = isValid(defaultValue) ? defaultValue : undefined;
  const [tokenValue, setValue, clearValue] = useLocalStorage(
    storageKey,
    checkedDefaultValue,
  );

  const setTokenValue = (value: string): void => {
    if (isValid(value)) {
      setValue(value);
    } else {
      clearValue();
    }
  };

  return [tokenValue, setTokenValue, clearValue];
};

export const useAccessTokenStorage = (
  defaultValue?: string,
): LocalStorageHookTuple => {
  const [value, setValue, clearValue] = useTokenStorage(
    `odin_${appConfig.isProd ? 'prod' : appConfig.appEnv}_access`,
    defaultValue,
  );
  return [value, setValue, clearValue];
};

export const useRefreshTokenStorage = (
  defaultValue?: string,
): LocalStorageHookTuple => {
  const [value, setValue, clearValue] = useTokenStorage(
    `odin_${appConfig.isProd ? 'prod' : appConfig.appEnv}_refresh`,
    defaultValue,
  );
  return [value, setValue, clearValue];
};

/**
 * Persisted marker that the user reached the app through the login process and
 * has not yet cleared the platform policy consent gate. Backed by localStorage
 * (not in-memory) so the gate survives a page refresh — the user must accept
 * the terms or sign out, and cannot bypass the screen by reloading. Set on
 * `signIn`, cleared on `signOut` and once there are no outstanding consents.
 */
export const useConsentGatePendingStorage = (): LocalStorageHookTuple => {
  return useLocalStorage(`odin_${appConfig.appEnv}_consent_gate_pending`);
};

export const useAuthenticationRef = (): [
  MutableRefObject<boolean>,
  (value: boolean) => void,
] => {
  const [accessTokenRef] = useAccessTokenStorage();
  const [refreshTokenRef] = useRefreshTokenStorage();

  const getInitialState = useCallback((): boolean => {
    const hasAccess =
      accessTokenRef.current != null &&
      accessTokenRef.current !== 'null' &&
      accessTokenRef.current !== 'undefined';
    const hasRefresh =
      refreshTokenRef.current != null &&
      refreshTokenRef.current !== 'null' &&
      refreshTokenRef.current !== 'undefined';

    return hasAccess && hasRefresh;
  }, [accessTokenRef.current, refreshTokenRef.current]);

  const authRef = useRef(getInitialState());

  const setAuthRef = (value: boolean): void => {
    authRef.current = value;
  };

  return [authRef, setAuthRef];
};

export const useAuth = (): AuthState => {
  const {
    fusionAuthBaseURL: fusionAuthURL,
    fusionAuthClientID,
    fusionAuthTenantID,
  } = appConfig;

  const [accessTokenRef, setAccessToken, clearAccessToken] =
    useAccessTokenStorage();
  const [refreshTokenRef, setRefreshToken, clearRefreshToken] =
    useRefreshTokenStorage();
  const [isAuthenticatedRef, setIsAuthenticated] = useAuthenticationRef();

  const sessionFetcherRef = React.useRef<SessionFetcher>();
  const [
    consentGatePendingRef,
    setConsentGatePending,
    clearConsentGatePending,
  ] = useConsentGatePendingStorage();

  return useMemo((): AuthState => {
    return {
      getIsAuthenticated: (): boolean => isAuthenticatedRef.current,
      getConsentGatePending: (): boolean =>
        consentGatePendingRef.current === 'true',
      clearConsentGatePending: (): void => clearConsentGatePending(),
      getLoginUrl: (): string =>
        `${window.location.protocol}//${window.location.hostname}${
          window.location.hostname.includes('localhost') ? ':3000' : ''
        }/oauth/login`,
      getAccessToken: (): string => accessTokenRef.current,
      refreshAccessToken: (): Promise<string> =>
        refreshJwt({
          refreshToken: refreshTokenRef.current,
          accessToken: accessTokenRef.current,
        }).then((resp) => {
          setAccessToken(resp.accessToken);
          setRefreshToken(resp.refreshToken);

          trackUserSession(SessionType.RefreshToken, resp.accessToken).then(
            () => {
              sessionFetcherRef.current?.fetchCurrentSession?.();
            },
          );
          return resp.accessToken;
        }),
      signOut: (): void => {
        clearSessionCookie();
        clearAccessToken();
        clearRefreshToken();
        clearConsentGatePending();
        setIsAuthenticated(false);

        if (window.location.pathname?.startsWith('/self-onboarding/')) {
          window.location.reload();
        } else {
          const redirectURL = `${window.location.protocol}//${
            window.location.hostname
          }${
            window.location.hostname.includes('localhost') ? ':3000' : ''
          }/oauth/logout`;
          logout({
            fusionAuthURL,
            fusionAuthClientID,
            fusionAuthTenantID,
            redirectURL,
          });
        }
      },
      signIn: (accessToken, refreshToken): void => {
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setJwtCookie({ accessToken });
        setIsAuthenticated(true);
        // Arm the policy consent gate only for a full OAuth login — the
        // office/admin path (AuthProvider), which always supplies a refresh
        // token. The other sign-in callers (self-onboarding, pending-change)
        // pass a null refresh token and run outside AppLayout, so they must
        // not arm the gate. Persisted so it survives a refresh: the user must
        // accept the terms or sign out and cannot dismiss the gate by reloading.
        if (refreshToken != null) {
          setConsentGatePending('true');
        }
        trackUserSession(SessionType.Password, accessToken);
      },
      initializeSessionFetcher: (sessionFetcher: SessionFetcher): void => {
        sessionFetcherRef.current = sessionFetcher;
      },
    };
  }, [
    isAuthenticatedRef,
    accessTokenRef,
    refreshTokenRef,
    sessionFetcherRef,
    consentGatePendingRef,
  ]);
};
